import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { ActiveWorkspaceProvider, useActiveWorkspace } from "./ActiveWorkspaceContext";
import WorkspaceShell from "./WorkspaceShell";
import { WorkspaceDashboard } from "./widgetRegistry";
import "./WorkspaceShell.css";
import CreatorStudioPage from "../ContentStudio/CreatorStudioPage";
import OrganizationStudioPage from "../ContentStudio/OrganizationStudioPage";
import ProjectStudioPage from "../ContentStudio/ProjectStudioPage";
import SellerWorkspacePanel from "../ContentStudio/SellerWorkspacePanel";
import FollowersWorkspacePanel from "../ContentStudio/FollowersWorkspacePanel";
import PartnerCommissionsPanel from "./PartnerCommissionsPanel";
import RecipientPayoutSettings from "./RecipientPayoutSettings";
import {
  fetchCreatorDashboard,
  fetchOrganizationDashboard,
  fetchProjectDashboard,
  fetchStudioNotifications,
} from "../ContentStudio/contentStudioApi";
import api from "../../Environment";
import {
  getWorkspace,
  writeStoredWorkspace,
  startWorkspaceHandoff,
} from "./workspaceRegistry";

async function fetchAdvertiserWorkspacePayload() {
  const empty = {
    advertiser_campaigns: [],
    advertiser_analytics: { impressions: 0, clicks: 0 },
  };
  try {
    const [campRes, analyticsRes] = await Promise.all([
      api.postMethod("studio/advertiser/campaigns", {}),
      api.postMethod("studio/advertiser/analytics", {}),
    ]);
    const campBody = campRes && campRes.data ? campRes.data : campRes;
    const analyticsBody =
      analyticsRes && analyticsRes.data ? analyticsRes.data : analyticsRes;
    const campaigns =
      campBody &&
      campBody.success &&
      campBody.data &&
      Array.isArray(campBody.data.items)
        ? campBody.data.items
        : [];
    const summary =
      analyticsBody && analyticsBody.success && analyticsBody.data
        ? analyticsBody.data.summary || {}
        : {};
    return {
      advertiser_campaigns: campaigns,
      advertiser_analytics: summary,
    };
  } catch (e) {
    return empty;
  }
}

const WORKSPACE_LOAD_ERROR = "Unable to load this workspace.";

function workspaceLoadFailed(body) {
  return !body || body.success === false;
}

function WorkspaceLoadError({ message }) {
  return (
    <div className="ws-panel">
      <p className="ws-muted">{message}</p>
    </div>
  );
}

function canOpenOps(ctx) {
  return !(ctx && ctx.caps) || !!ctx.caps.can_access_ops;
}

function entitySlugFromPayload(payload, kind) {
  if (!payload) return null;
  if (kind === "creator" && payload.creator && payload.creator.slug) {
    return payload.creator.slug;
  }
  if (kind === "organization" && payload.organization && payload.organization.slug) {
    return payload.organization.slug;
  }
  if (kind === "project") {
    const p = payload.project || payload;
    return p && p.slug ? p.slug : null;
  }
  return null;
}

function openEntityModule(kind, action, entityId, slug, extras) {
  if (typeof window === "undefined") return false;
  const extra = extras || {};

  if (action === "media" || action === "videos") {
    let q = "";
    if (kind === "creator" && entityId) {
      q = "creator_id=" + encodeURIComponent(entityId);
    } else if (kind === "organization" && entityId) {
      q = "organization_id=" + encodeURIComponent(entityId);
    } else if (kind === "project") {
      // Videos have no project_id; scope via linked org/creator when present.
      if (extra.organizationId) {
        q = "organization_id=" + encodeURIComponent(extra.organizationId);
      } else if (extra.creatorId) {
        q = "creator_id=" + encodeURIComponent(extra.creatorId);
      } else {
        return false;
      }
    }
    if (!q) return false;
    window.location.assign("/studio/library?" + q);
    return true;
  }

  if (action === "storefront" || action === "products") {
    // Handled as in-workspace sections (SellerWorkspacePanel) — not Admin.
    return false;
  }

  if (action === "followers") {
    // Managed in-workspace (FollowersWorkspacePanel).
    return false;
  }

  if (action === "public") {
    if (slug) {
      const base =
        kind === "creator"
          ? "/creator/"
          : kind === "organization"
            ? "/organization/"
            : "/project/";
      window.location.assign(base + slug);
      return true;
    }
  }

  if (action === "donations" || action === "funding") {
    // Stay in workspace — donation configuration is edited in-studio
    // (same Canonical Entity Configuration as Admin).
    return false;
  }

  return false;
}

function withProvider(Page) {
  return function Wrapped(props) {
    return (
      <ActiveWorkspaceProvider>
        <Page {...props} />
      </ActiveWorkspaceProvider>
    );
  };
}

function PersonalWorkspacePageInner() {
  // Personal workspace removed — ordinary account tools live under /account.
  return <Redirect to="/account" />;
}

function CreatorWorkspacePageInner(props) {
  const ctx = useActiveWorkspace();
  const routeId =
    (props.match && props.match.params && props.match.params.creatorId) || "";
  const [section, setSection] = useState("dashboard");
  const [entityLabel, setEntityLabel] = useState("");
  const [payload, setPayload] = useState({});
  const [entitySlug, setEntitySlug] = useState("");
  const [loadError, setLoadError] = useState("");
  const creatorId =
    routeId ||
    (ctx && ctx.workspaceId === "creator" && ctx.entityId) ||
    (ctx &&
      ctx.caps &&
      ctx.caps.creator_ids &&
      String(ctx.caps.creator_ids[0])) ||
    "";

  const handleSection = (s) => {
    const action = String(s).replace(/^soon:/, "");
    // Biography / mission edits the public profile fields.
    if (action === "mission") {
      setSection("profile");
      return;
    }
    if (action === "donations") {
      setSection("donations");
      return;
    }
    if (
      openEntityModule(
        "creator",
        action,
        creatorId,
        entitySlug || entitySlugFromPayload(payload, "creator")
      )
    ) {
      return;
    }
    setSection(s);
  };

  useEffect(() => {
    if (!ctx || !ctx.ready) return;
    if (!creatorId && ctx.caps && ctx.caps.creator_ids && ctx.caps.creator_ids.length > 1) {
      // Multi-entity: stay on picker (no auto-pick)
      return;
    }
    if (!creatorId && ctx.caps && ctx.caps.creator_ids && ctx.caps.creator_ids[0]) {
      props.history.replace(
        "/workspace/creator/" + ctx.caps.creator_ids[0]
      );
      return;
    }
    if (creatorId) {
      ctx.activate("creator", creatorId, entityLabel || null);
      writeStoredWorkspace("creator", creatorId, entityLabel || null);
      setLoadError("");
      fetchCreatorDashboard(creatorId).then((body) => {
        if (workspaceLoadFailed(body)) {
          setLoadError(WORKSPACE_LOAD_ERROR);
          return;
        }
        const name =
          body && body.creator && (body.creator.display_name || body.creator.name);
        const slug = body && body.creator && body.creator.slug;
        if (slug) setEntitySlug(slug);
        if (name) {
          setEntityLabel(name);
          ctx.activate("creator", creatorId, name);
        }
        fetchStudioNotifications({ creator_id: creatorId }).then((notes) => {
          setPayload({
            ...(body || {}),
            notifications: notes,
            recent_media: (body && body.recent) || (body && body.recent_media),
            analytics: (body && body.analytics) || {},
          });
        });
      }).catch(() => setLoadError(WORKSPACE_LOAD_ERROR));
    }
  }, [ctx && ctx.ready, creatorId]);

  if (
    ctx &&
    ctx.ready &&
    !creatorId &&
    ctx.caps &&
    ctx.caps.creator_ids &&
    ctx.caps.creator_ids.length > 1
  ) {
    return (
      <WorkspaceShell workspaceId="creator" section={section} onSelectSection={handleSection}>
        <div className="ws-panel">
          <h2>Choose Creator</h2>
          <p className="ws-muted">Select which creator page to manage.</p>
          <ul className="ws-widget__list">
            {ctx.caps.creator_ids.map((id) => (
              <li key={id}>
                <a className="ws-btn" href={"/workspace/creator/" + id}>
                  Creator #{id}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </WorkspaceShell>
    );
  }

  if (ctx && ctx.ready && !creatorId) {
    return (
      <WorkspaceShell workspaceId="creator" section={section} onSelectSection={handleSection}>
        <div className="ws-panel">
          <p className="ws-muted">No creator workspace is linked to this account.</p>
        </div>
      </WorkspaceShell>
    );
  }

  const liveSections = [
    "dashboard",
    "profile",
    "media",
    "community",
    "analytics",
    "integrations",
    "notifications",
    "settings",
    "donations",
  ];
  const studioSection =
    section === "dashboard"
      ? "dashboard"
      : section === "donations"
        ? "donations"
        : liveSections.indexOf(section) >= 0
          ? section
          : "dashboard";
  const commerceTab = section === "products" ? "products" : "home";

  return (
    <WorkspaceShell
      workspaceId="creator"
      entityLabel={entityLabel}
      section={section}
      onSelectSection={handleSection}
      entityOptions={(ctx && ctx.caps && ctx.caps.creator_ids) || []}
      entityType="creator"
      quickActions={[
        {
          label: "Media Library",
          href: "/studio/library?creator_id=" + encodeURIComponent(creatorId),
        },
        { label: "Timeline", href: "/studio/timeline" },
      ]}
    >
      {loadError ? (
        <WorkspaceLoadError message={loadError} />
      ) : section === "dashboard" ? (
        <WorkspaceDashboard
          workspace={getWorkspace("creator")}
          payload={payload}
        />
      ) : section === "followers" ? (
        <FollowersWorkspacePanel
          entityType="creator"
          entityId={creatorId}
          entityLabel={entityLabel}
        />
      ) : section === "storefront" || section === "products" ? (
        <SellerWorkspacePanel
          sellerType="creator"
          sellerId={creatorId}
          initialTab={commerceTab}
        />
      ) : creatorId && liveSections.indexOf(studioSection) >= 0 ? (
        <CreatorStudioPage
          embedded
          creatorId={creatorId}
          section={studioSection === "dashboard" ? "profile" : studioSection}
          onSectionChange={handleSection}
          match={{ params: { creatorId } }}
          history={props.history}
        />
      ) : (
        <div className="ws-panel">
          <p className="ws-muted">Select a tool from the sidebar.</p>
        </div>
      )}
    </WorkspaceShell>
  );
}

function OrganizationWorkspacePageInner(props) {
  const ctx = useActiveWorkspace();
  const routeId =
    (props.match && props.match.params && props.match.params.organizationId) ||
    "";
  const [section, setSection] = useState("dashboard");
  const [entityLabel, setEntityLabel] = useState("");
  const [payload, setPayload] = useState({});
  const [entitySlug, setEntitySlug] = useState("");
  const [loadError, setLoadError] = useState("");
  const orgId =
    routeId ||
    (ctx && ctx.workspaceId === "organization" && ctx.entityId) ||
    (ctx &&
      ctx.caps &&
      ctx.caps.organization_ids &&
      ctx.caps.organization_ids.length === 1 &&
      String(ctx.caps.organization_ids[0])) ||
    "";

  const handleSection = (s) => {
    const action = String(s).replace(/^soon:/, "");
    if (action === "mission" || action === "profile" || action === "settings") {
      setSection(action === "mission" ? "profile" : action === "settings" ? "settings" : "profile");
      return;
    }
    if (action === "public") {
      if (
        openEntityModule(
          "organization",
          "public",
          orgId,
          entitySlug || entitySlugFromPayload(payload, "organization")
        )
      ) {
        return;
      }
    }
    if (action === "followers") {
      setSection("followers");
      return;
    }
    if (action === "donations") {
      setSection("donations");
      return;
    }
    if (
      openEntityModule(
        "organization",
        action,
        orgId,
        entitySlug || entitySlugFromPayload(payload, "organization")
      )
    ) {
      return;
    }
    setSection(s);
  };

  useEffect(() => {
    if (!ctx || !ctx.ready) return;
    if (
      !orgId &&
      ctx.caps &&
      ctx.caps.organization_ids &&
      ctx.caps.organization_ids.length === 1
    ) {
      props.history.replace(
        "/workspace/organization/" + ctx.caps.organization_ids[0]
      );
      return;
    }
    if (orgId) {
      ctx.activate("organization", orgId, entityLabel || null);
      setLoadError("");
      fetchOrganizationDashboard(orgId).then((body) => {
        if (workspaceLoadFailed(body)) {
          setLoadError(WORKSPACE_LOAD_ERROR);
          return;
        }
        const name =
          body && body.organization && body.organization.name;
        const slug = body && body.organization && body.organization.slug;
        if (slug) setEntitySlug(slug);
        if (name) {
          setEntityLabel(name);
          ctx.activate("organization", orgId, name);
        }
        fetchStudioNotifications({ organization_id: orgId }).then((notes) => {
          setPayload({
            ...(body || {}),
            notifications: notes,
            recent_media: (body && body.recent) || [],
            analytics: (body && body.analytics) || {},
            team: (body && body.members) || (body && body.team) || [],
          });
        });
      }).catch(() => setLoadError(WORKSPACE_LOAD_ERROR));
    }
  }, [ctx && ctx.ready, orgId]);

  if (
    ctx &&
    ctx.ready &&
    !orgId &&
    ctx.caps &&
    ctx.caps.organization_ids &&
    ctx.caps.organization_ids.length > 1
  ) {
    return (
      <WorkspaceShell workspaceId="organization" section={section} onSelectSection={handleSection}>
        <div className="ws-panel">
          <h2>Choose Organization</h2>
          <ul className="ws-widget__list">
            {ctx.caps.organization_ids.map((id) => (
              <li key={id}>
                <a className="ws-btn" href={"/workspace/organization/" + id}>
                  Organization #{id}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </WorkspaceShell>
    );
  }

  if (ctx && ctx.ready && !orgId) {
    return (
      <WorkspaceShell workspaceId="organization" section={section} onSelectSection={handleSection}>
        <div className="ws-panel">
          <p className="ws-muted">No organization workspace is linked to this account.</p>
        </div>
      </WorkspaceShell>
    );
  }

  const libHref = orgId
    ? "/studio/library?organization_id=" + encodeURIComponent(orgId)
    : "/studio/library";

  const orgLiveSections = [
    "community",
    "analytics",
    "team",
    "settings",
    "profile",
    "mission",
    "donations",
  ];
  const orgCommerceTab = section === "products" ? "products" : "home";

  return (
    <WorkspaceShell
      workspaceId="organization"
      entityLabel={entityLabel}
      section={section}
      onSelectSection={handleSection}
      entityOptions={(ctx && ctx.caps && ctx.caps.organization_ids) || []}
      entityType="organization"
      quickActions={[
        { label: "Media Library", href: libHref },
        canOpenOps(ctx) ? { label: "Livestreams", href: "/ops" } : null,
      ].filter(Boolean)}
    >
      {loadError ? (
        <WorkspaceLoadError message={loadError} />
      ) : section === "dashboard" ? (
        <WorkspaceDashboard
          workspace={getWorkspace("organization")}
          payload={payload}
        />
      ) : section === "followers" ? (
        <FollowersWorkspacePanel
          entityType="organization"
          entityId={orgId}
          entityLabel={entityLabel}
        />
      ) : section === "storefront" || section === "products" ? (
        <SellerWorkspacePanel
          sellerType="organization"
          sellerId={orgId}
          initialTab={orgCommerceTab}
        />
      ) : orgId && orgLiveSections.indexOf(section) >= 0 ? (
        <OrganizationStudioPage
          embedded
          organizationId={orgId}
          section={section === "mission" ? "profile" : section}
          match={{ params: { organizationId: orgId } }}
          history={props.history}
        />
      ) : (
        <div className="ws-panel">
          <p className="ws-muted">Select a tool from the sidebar.</p>
        </div>
      )}
    </WorkspaceShell>
  );
}

function ProjectWorkspacePageInner(props) {
  const ctx = useActiveWorkspace();
  const routeId =
    (props.match && props.match.params && props.match.params.projectId) || "";
  const [section, setSection] = useState("dashboard");
  const [entityLabel, setEntityLabel] = useState("");
  const [payload, setPayload] = useState({});
  const [entitySlug, setEntitySlug] = useState("");
  const projectId =
    routeId ||
    (ctx && ctx.workspaceId === "project" && ctx.entityId) ||
    (ctx &&
      ctx.caps &&
      ctx.caps.project_ids &&
      ctx.caps.project_ids.length === 1 &&
      String(ctx.caps.project_ids[0])) ||
    "";

  const [linkedOrgId, setLinkedOrgId] = useState("");
  const [linkedCreatorId, setLinkedCreatorId] = useState("");
  const [loadError, setLoadError] = useState("");

  const handleSection = (s) => {
    const action = String(s).replace(/^soon:/, "");
    if (action === "mission" || action === "settings") {
      setSection("profile");
      return;
    }
    if (action === "funding" || action === "donations") {
      setSection("funding");
      return;
    }
    if (action === "updates" || action === "community") {
      setSection("updates");
      return;
    }
    if (
      openEntityModule(
        "project",
        action === "funding" ? "donations" : action,
        projectId,
        entitySlug || entitySlugFromPayload(payload, "project"),
        { organizationId: linkedOrgId, creatorId: linkedCreatorId }
      )
    ) {
      return;
    }
    setSection(s);
  };

  useEffect(() => {
    if (!ctx || !ctx.ready) return;
    if (
      !projectId &&
      ctx.caps &&
      ctx.caps.project_ids &&
      ctx.caps.project_ids.length === 1
    ) {
      props.history.replace("/workspace/project/" + ctx.caps.project_ids[0]);
      return;
    }
    if (projectId && typeof fetchProjectDashboard === "function") {
      ctx.activate("project", projectId, entityLabel || null);
      setLoadError("");
      Promise.resolve(fetchProjectDashboard(projectId))
        .then((body) => {
          if (workspaceLoadFailed(body)) {
            setLoadError(WORKSPACE_LOAD_ERROR);
            setPayload({});
            return;
          }
          const data = body && body.data ? body.data : body;
          const name =
            data && data.project && (data.project.title || data.project.name);
          const slug = data && data.project && data.project.slug;
          if (slug) setEntitySlug(slug);
          if (data && data.project) {
            if (data.project.organization_id) {
              setLinkedOrgId(String(data.project.organization_id));
            }
            if (data.project.creator_id) {
              setLinkedCreatorId(String(data.project.creator_id));
            }
          }
          if (name) {
            setEntityLabel(name);
            ctx.activate("project", projectId, name);
          }
          setPayload(data || {});
        })
        .catch(() => {
          setLoadError(WORKSPACE_LOAD_ERROR);
          setPayload({});
        });
    }
  }, [ctx && ctx.ready, projectId]);

  if (
    ctx &&
    ctx.ready &&
    !projectId &&
    ctx.caps &&
    ctx.caps.project_ids &&
    ctx.caps.project_ids.length > 1
  ) {
    return (
      <WorkspaceShell workspaceId="project" section={section} onSelectSection={handleSection} hiddenNav={["media"]}>
        <div className="ws-panel">
          <h2>Choose Project</h2>
          <ul className="ws-widget__list">
            {ctx.caps.project_ids.map((id) => (
              <li key={id}>
                <a className="ws-btn" href={"/workspace/project/" + id}>
                  Project #{id}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </WorkspaceShell>
    );
  }

  if (ctx && ctx.ready && !projectId) {
    return (
      <WorkspaceShell workspaceId="project" section={section} onSelectSection={handleSection} hiddenNav={["media"]}>
        <div className="ws-panel">
          <p className="ws-muted">No project workspace is linked to this account.</p>
        </div>
      </WorkspaceShell>
    );
  }

  const projectLibHref = linkedOrgId
    ? "/studio/library?organization_id=" + encodeURIComponent(linkedOrgId)
    : linkedCreatorId
      ? "/studio/library?creator_id=" + encodeURIComponent(linkedCreatorId)
      : null;

  const projectLive = ["profile", "funding", "updates", "analytics", "milestones"];

  return (
    <WorkspaceShell
      workspaceId="project"
      entityLabel={entityLabel}
      section={section}
      onSelectSection={handleSection}
      entityOptions={(ctx && ctx.caps && ctx.caps.project_ids) || []}
      entityType="project"
      hiddenNav={projectLibHref ? [] : ["media"]}
      quickActions={[
        projectLibHref
          ? { label: "Media Library", href: projectLibHref }
          : null,
        canOpenOps(ctx) ? { label: "Livestreams", href: "/ops" } : null,
        { label: "Schedules", href: "/studio/timeline" },
      ].filter(Boolean)}
    >
      {loadError ? (
        <WorkspaceLoadError message={loadError} />
      ) : section === "dashboard" ? (
        <WorkspaceDashboard
          workspace={getWorkspace("project")}
          payload={payload}
        />
      ) : projectId && projectLive.indexOf(section) >= 0 ? (
        <ProjectStudioPage
          embedded
          projectId={projectId}
          section={section}
          match={{ params: { projectId } }}
          history={props.history}
        />
      ) : (
        <div className="ws-panel">
          <p className="ws-muted">Select a tool from the sidebar.</p>
        </div>
      )}
    </WorkspaceShell>
  );
}

function AdvertiserWorkspacePageInner() {
  const ctx = useActiveWorkspace();
  const [section, setSection] = useState("campaigns");
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!ctx || !ctx.ready) return;
    ctx.activate("advertiser", null, null);
    writeStoredWorkspace("advertiser", null, null);
    let cancelled = false;
    setError("");
    fetchAdvertiserWorkspacePayload().then((data) => {
      if (!cancelled) setPayload(data);
    });
    return () => {
      cancelled = true;
    };
  }, [ctx && ctx.ready]);

  if (!ctx || !ctx.ready) {
    return (
      <div className="ws-shell">
        <p className="ws-muted" style={{ padding: "2rem" }}>
          Loading workspace…
        </p>
      </div>
    );
  }

  const available = (ctx.available || []).some((w) => w.id === "advertiser");
  if (!available && !(ctx.caps && ctx.caps.is_platform_admin)) {
    return (
      <WorkspaceShell workspaceId="advertiser" section={section} onSelectSection={setSection}>
        <div className="ws-panel">
          <p className="ws-muted">
            Advertiser workspace is not linked to this account.
          </p>
        </div>
      </WorkspaceShell>
    );
  }

  return (
    <WorkspaceShell
      workspaceId="advertiser"
      section={section}
      onSelectSection={setSection}
    >
      {error ? <p className="ws-muted">{error}</p> : null}
      {payload == null ? (
        <p className="ws-muted">Loading campaigns…</p>
      ) : (
        <WorkspaceDashboard workspace={getWorkspace("advertiser")} payload={payload} />
      )}
    </WorkspaceShell>
  );
}

function PartnerWorkspacePageInner() {
  const ctx = useActiveWorkspace();
  const [section, setSection] = useState("commissions");
  const [partnerId, setPartnerId] = useState(null);

  useEffect(() => {
    if (!ctx || !ctx.ready) return;
    ctx.activate("partner", null, null);
    writeStoredWorkspace("partner", null, null);
  }, [ctx && ctx.ready]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.postMethod("studio/partner/commissions", {});
        const body = res && res.data ? res.data : res;
        if (
          !cancelled &&
          body &&
          body.success &&
          body.data &&
          body.data.partner_id
        ) {
          setPartnerId(body.data.partner_id);
        }
      } catch (e) {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!ctx || !ctx.ready) {
    return (
      <div className="ws-shell">
        <p className="ws-muted" style={{ padding: "2rem" }}>
          Loading workspace…
        </p>
      </div>
    );
  }

  const available = (ctx.available || []).some((w) => w.id === "partner");
  if (!available && !(ctx.caps && ctx.caps.is_platform_admin)) {
    return (
      <WorkspaceShell workspaceId="partner" section={section} onSelectSection={setSection}>
        <div className="ws-panel">
          <p className="ws-muted">Partner workspace is not linked to this account.</p>
        </div>
      </WorkspaceShell>
    );
  }

  return (
    <WorkspaceShell workspaceId="partner" section={section} onSelectSection={setSection}>
      {section === "settings" ? (
        <div>
          <div className="ws-panel">
            <h2>Partner settings</h2>
            <p className="ws-muted">
              The Partner referral commission rate is set by the platform and may
              currently be 0%. Update payout destination below.
            </p>
          </div>
          <RecipientPayoutSettings
            payableType="partner"
            payableId={partnerId}
            workspace="partner"
          />
        </div>
      ) : (
        <PartnerCommissionsPanel />
      )}
    </WorkspaceShell>
  );
}

function StaffWorkspacePageInner(props) {
  const kind =
    (props.match && props.match.params && props.match.params.staffKind) ||
    props.staffKind ||
    "sales";
  const workspace = getWorkspace(kind) || getWorkspace("sales");
  const ctx = useActiveWorkspace();
  const [status, setStatus] = useState("opening");
  const [error, setError] = useState("");
  const entitled =
    !!ctx &&
    !!ctx.ready &&
    !!workspace &&
    (ctx.available || []).some((w) => w.id === workspace.id);

  useEffect(() => {
    if (!ctx || !ctx.ready || !workspace) return;
    if (!entitled) return;
    ctx.activate(workspace.id, null, null);
    let cancelled = false;
    setStatus("opening");
    setError("");
    startWorkspaceHandoff(workspace)
      .then(() => {
        if (!cancelled) setStatus("handoff");
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("error");
          setError("Unable to open the staff portal. Retry or contact support.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [ctx && ctx.ready, workspace && workspace.id, entitled]);

  if (!ctx || !ctx.ready) {
    return (
      <div className="ws-shell">
        <p className="ws-muted" style={{ padding: "2rem" }}>
          Loading workspace…
        </p>
      </div>
    );
  }

  if (!entitled) {
    return (
      <div className="ws-shell" style={{ display: "block", padding: "2.5rem 1.5rem" }}>
        <div className="ws-panel">
          <p className="ws-muted">This workspace is not linked to this account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ws-shell" style={{ display: "block", padding: "2.5rem 1.5rem" }}>
      <h1 style={{ fontFamily: "Fraunces, Georgia, serif", margin: "0 0 0.5rem" }}>
        {workspace ? workspace.displayName : "Workspace"}
      </h1>
      {status === "opening" || status === "handoff" ? (
        <p className="ws-muted">Opening {workspace.displayName}…</p>
      ) : null}
      {status === "error" ? (
        <div className="ws-panel">
          <p className="ws-muted">{error}</p>
          <button
            type="button"
            className="ws-btn"
            onClick={() => startWorkspaceHandoff(workspace)}
          >
            Retry handoff
          </button>
        </div>
      ) : null}
    </div>
  );
}

function WorkspaceHomeInner(props) {
  const ctx = useActiveWorkspace();
  if (!ctx || !ctx.ready) {
    return <div className="ws-shell"><p className="ws-muted" style={{ padding: "2rem" }}>Loading workspace…</p></div>;
  }
  const stored = ctx.workspaceId;
  const allowed = ctx.available || [];
  const match = allowed.find((w) => w.id === stored) || allowed[0];
  if (match) {
    let path = match.route;
    if (match.entityType === "creator" && ctx.caps && ctx.caps.creator_ids[0]) {
      path = match.route + "/" + ctx.caps.creator_ids[0];
    } else if (
      match.entityType === "organization" &&
      ctx.caps &&
      ctx.caps.organization_ids[0]
    ) {
      path = match.route + "/" + ctx.caps.organization_ids[0];
    } else if (
      match.entityType === "project" &&
      ctx.caps &&
      ctx.caps.project_ids[0]
    ) {
      path = match.route + "/" + ctx.caps.project_ids[0];
    }
    return <Redirect to={path} />;
  }
  return <Redirect to="/account" />;
}

export const PersonalWorkspacePage = withProvider(PersonalWorkspacePageInner);
export const CreatorWorkspacePage = withProvider(CreatorWorkspacePageInner);
export const OrganizationWorkspacePage = withProvider(
  OrganizationWorkspacePageInner
);
export const ProjectWorkspacePage = withProvider(ProjectWorkspacePageInner);
export const SalesWorkspacePage = withProvider(function SalesPage(p) {
  return <StaffWorkspacePageInner {...p} staffKind="sales" />;
});
export const ModeratorWorkspacePage = withProvider(function ModPage(p) {
  return <StaffWorkspacePageInner {...p} staffKind="moderator" />;
});
export const SubadminWorkspacePage = withProvider(function SubPage(p) {
  return <StaffWorkspacePageInner {...p} staffKind="subadmin" />;
});
export const AdvertiserWorkspacePage = withProvider(AdvertiserWorkspacePageInner);
export const PartnerWorkspacePage = withProvider(PartnerWorkspacePageInner);
export const WorkspaceHomePage = withProvider(WorkspaceHomeInner);
